import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLocalization } from '../../contexts/LocalizationContext';
import customerService from '../../services/customerService';
import Loading from '../../components/Common/Loading';
import Button from '../../components/Common/Button';
import Table from '../../components/Common/Table';

const PLATFORM_COLORS = {
  web: { bg: 'rgba(16,185,129,0.12)', color: '#059669' },
  ios: { bg: 'rgba(99,102,241,0.12)', color: '#4338ca' },
  android: { bg: 'rgba(34,197,94,0.12)', color: '#16a34a' },
  mobile: { bg: 'rgba(99,102,241,0.12)', color: '#4338ca' },
};

const mobileBadgeLabel = (lastPlatform, isRtl) => {
  if (lastPlatform === 'ios') return isRtl ? 'آيفون (iOS)' : 'iPhone (iOS)';
  if (lastPlatform === 'android') return isRtl ? 'أندرويد' : 'Android';
  return isRtl ? 'تطبيق الجوال' : 'Mobile App';
};

const PlatformBadges = ({ usesWeb, usesMobile, lastPlatform, isRtl }) => {
  if (!usesWeb && !usesMobile) {
    return <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{isRtl ? 'لا يوجد نشاط' : 'No activity yet'}</span>;
  }
  const mobileColors = PLATFORM_COLORS[lastPlatform] || PLATFORM_COLORS.mobile;
  return (
    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
      {usesWeb && (
        <span className="badge" style={{ background: PLATFORM_COLORS.web.bg, color: PLATFORM_COLORS.web.color, fontWeight: 600 }}>
          {isRtl ? 'الموقع' : 'Website'}
        </span>
      )}
      {usesMobile && (
        <span className="badge" style={{ background: mobileColors.bg, color: mobileColors.color, fontWeight: 600 }}>
          {mobileBadgeLabel(lastPlatform, isRtl)}
        </span>
      )}
    </div>
  );
};

const formatDate = (value, isRtl) => {
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString(isRtl ? 'ar-SA' : 'en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
};

const CustomersIndex = () => {
  const { isRtl } = useLocalization();
  const navigate = useNavigate();
  const [customers, setCustomers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [platformFilter, setPlatformFilter] = useState('all');

  useEffect(() => { loadCustomers(); }, []);

  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      const data = await customerService.getAll();
      setCustomers(data);
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredCustomers = customers.filter((c) => {
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      const match =
        c.fullName?.toLowerCase().includes(s) ||
        c.phoneE164?.toLowerCase().includes(s) ||
        c.city?.toLowerCase().includes(s);
      if (!match) return false;
    }
    if (platformFilter === 'web' && !c.usesWeb) return false;
    if (platformFilter === 'mobile' && !c.usesMobile) return false;
    if (platformFilter === 'ios' && c.lastPlatform !== 'ios') return false;
    if (platformFilter === 'android' && c.lastPlatform !== 'android') return false;
    return true;
  });

  const columns = [
    {
      header: isRtl ? 'الاسم' : 'Name',
      accessor: 'fullName',
      render: (value) => value || (isRtl ? 'بدون اسم' : 'Unnamed'),
    },
    {
      header: isRtl ? 'رقم الهاتف' : 'Phone',
      accessor: 'phoneE164',
      render: (value) => <span className="ltr-text">{value}</span>,
    },
    {
      header: isRtl ? 'المدينة' : 'City',
      accessor: 'city',
      render: (value) => value || '—',
    },
    {
      header: isRtl ? 'موثّق' : 'Verified',
      accessor: 'isVerified',
      render: (value) => (
        <span className={`badge ${value ? 'badge-success' : 'badge-neutral'}`}>
          {value ? (isRtl ? 'موثّق' : 'Verified') : (isRtl ? 'غير موثّق' : 'Unverified')}
        </span>
      ),
    },
    {
      header: isRtl ? 'المنصة المستخدمة' : 'Platform Used',
      accessor: 'usesWeb',
      render: (_, row) => <PlatformBadges usesWeb={row.usesWeb} usesMobile={row.usesMobile} lastPlatform={row.lastPlatform} isRtl={isRtl} />,
    },
    {
      header: isRtl ? 'الطلبات' : 'Orders',
      accessor: 'ordersCount',
    },
    {
      header: isRtl ? 'تاريخ التسجيل' : 'Registered',
      accessor: 'createdAt',
      render: (value) => formatDate(value, isRtl),
    },
    {
      header: isRtl ? 'إجراءات' : 'Actions',
      accessor: 'id',
      render: (value) => (
        <Button size="small" variant="secondary" onClick={() => navigate(`/customers/${value}`)}>
          {isRtl ? 'عرض' : 'View'}
        </Button>
      ),
    },
  ];

  const totalCount = customers.length;
  const verifiedCount = customers.filter((c) => c.isVerified).length;
  const webCount = customers.filter((c) => c.usesWeb).length;
  const mobileCount = customers.filter((c) => c.usesMobile).length;

  if (isLoading) {
    return <Loading text={isRtl ? 'جاري التحميل...' : 'Loading customers...'} />;
  }

  return (
    <div className="customers-page">
      <div className="page-header">
        <div className="page-header-content">
          <div className="page-header-main">
            <h1 className="page-title">{isRtl ? 'العملاء' : 'Customers'}</h1>
            <p className="page-description">
              {isRtl
                ? 'عملاء المتجر وتفاصيلهم، وما إذا كانوا يستخدمون الموقع أو تطبيق الجوال'
                : 'Storefront customers and their details, including whether they use the website or the mobile app'}
            </p>
          </div>
          <div className="page-header-actions">
            <Button variant="secondary" onClick={loadCustomers} disabled={isLoading}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="23 4 23 10 17 10"></polyline>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"></path>
              </svg>
              <span className="hidden-mobile">{isRtl ? 'تحديث' : 'Refresh'}</span>
            </Button>
          </div>
        </div>
      </div>

      <div className="stats-grid mb-6">
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-info">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"></path>
                <circle cx="9" cy="7" r="4"></circle>
                <path d="M23 21v-2a4 4 0 0 0-3-3.87"></path>
                <path d="M16 3.13a4 4 0 0 1 0 7.75"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{totalCount}</div>
              <div className="stat-label">{isRtl ? 'إجمالي العملاء' : 'Total Customers'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon stat-icon-success">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                <polyline points="22 4 12 14.01 9 11.01"></polyline>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{verifiedCount}</div>
              <div className="stat-label">{isRtl ? 'موثّقون' : 'Verified'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon" style={{ background: PLATFORM_COLORS.web.bg, color: PLATFORM_COLORS.web.color }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <line x1="2" y1="12" x2="22" y2="12"></line>
                <path d="M12 2a15.3 15.3 0 0 1 4 10 15.3 15.3 0 0 1-4 10 15.3 15.3 0 0 1-4-10 15.3 15.3 0 0 1 4-10z"></path>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{webCount}</div>
              <div className="stat-label">{isRtl ? 'يستخدمون الموقع' : 'Use Website'}</div>
            </div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-card-content">
            <div className="stat-icon" style={{ background: PLATFORM_COLORS.mobile.bg, color: PLATFORM_COLORS.mobile.color }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="7" y="2" width="10" height="20" rx="2" ry="2"></rect>
                <line x1="11" y1="18" x2="13" y2="18"></line>
              </svg>
            </div>
            <div className="stat-details">
              <div className="stat-number">{mobileCount}</div>
              <div className="stat-label">{isRtl ? 'يستخدمون التطبيق' : 'Use Mobile App'}</div>
            </div>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header customers-filters">
          <div className="search-box">
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="11" cy="11" r="8"></circle>
              <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
            </svg>
            <input
              type="text"
              placeholder={isRtl ? 'بحث بالاسم أو الهاتف أو المدينة...' : 'Search by name, phone or city...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <select
            className="form-select"
            style={{ maxWidth: 180 }}
            value={platformFilter}
            onChange={(e) => setPlatformFilter(e.target.value)}
          >
            <option value="all">{isRtl ? 'كل المنصات' : 'All Platforms'}</option>
            <option value="web">{isRtl ? 'الموقع' : 'Website'}</option>
            <option value="mobile">{isRtl ? 'تطبيق الجوال (الكل)' : 'Mobile App (any)'}</option>
            <option value="ios">{isRtl ? 'آيفون (iOS)' : 'iPhone (iOS)'}</option>
            <option value="android">{isRtl ? 'أندرويد' : 'Android'}</option>
          </select>
          <span className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>
            {isRtl ? `${filteredCustomers.length} عميل` : `${filteredCustomers.length} customers`}
          </span>
        </div>
        <div className="card-body p-0">
          <Table
            columns={columns}
            data={filteredCustomers}
            onRowClick={(row) => navigate(`/customers/${row.id}`)}
            emptyMessage={isRtl ? 'لا يوجد عملاء' : 'No customers found'}
          />
        </div>
      </div>

      <style>{`
        .customers-filters {
          flex-wrap: wrap;
          gap: var(--sp-3);
          align-items: center;
        }
      `}</style>
    </div>
  );
};

export default CustomersIndex;
